/* eslint-disable no-empty,object-curly-newline,no-nested-ternary,max-len */
/* global G, React */
import { useCallback, useContext, useEffect, useMemo, useState, useTransition } from 'react'
import { TextField } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import PlatformEvent from 'lib/util/event'
import {
  getDateFormatFromLocale,
  getDateMaskFromFormat,
  getDateWithoutTimezone,
  isOlderThan,
  sanitizeDateToMask,
} from 'lib/util/date'
import useMemoRef from '@platform/react/hook/useMemoRef'
import ApplicationContext from 'platform/react/context/application'
import Icon from 'ui/Element/Icon'

/**
 * Modal DatePicker
 *
 * {@link TextField} that makes use of Material-UI's {@link DatePicker} to display a modal to
 * facilitate the selection of a date.
 *
 * https://mui.com/x/react-date-pickers/getting-started/
 *
 * @param {Object} forwardedRef           a reference to the root element
 * @param {string} value                  the current input's value
 * @param {Object} events                 an object containing an onChange event handler
 * @param {function} events.onChange      a handler executed whenever a date is set
 * @param {boolean} [fromToday]           if only dates from today onwards can be selected
 * @param {boolean} [untilToday]          if only dates up to today can be selected
 * @param {boolean} [disabled]            if the component is disabled
 * @param {Object} [props]                additional component's properties
 * @param {string} [props.placeholder]    text to show when field is empty
 * @param {Object} [props.mask]           custom mask, e.g. __/__/____ __:__ or __/__/____ __:__ _M
 * @param {Object} [props.format]         date format string, e.g. dd/MM/yyyy
 * @param {string} [props.label]          a string to be used as the input's label
 * @param {boolean} [props.defaultToday]  whether the field should start with today's date set
 * @param {string} [props.minDate]        the minimum date allowed to be selected
 * @param {string} [props.maxDate]        the maximum date allowed to be selected
 * @param {boolean} [props.focused]       whether the field should start focused
 * @param {string} [props.helperText]     information text to display under the field
 * @param {boolean} [props.error]         whether to display the field in error state
 * @returns {JSX.Element}                 a new DatePickerField component
 */
const DatePickerField = ({ forwardedRef, value, events, fromToday, untilToday, disabled,
  ...props }) => {
  const { session } = useContext(ApplicationContext)
  const currentLanguage = useMemo(() => session[G.LANGUAGE], [session])

  const { invalidLabel, outOfRangeLabel } = props

  const [, startTransition] = useTransition()
  const now = new Date(Date.now())
  const defaultDate = props.defaultToday ? now : null
  const startDate = useMemo(() => (value
    ? new Date(value)
    : defaultDate
  ), [value])

  const defaultErrorState = { error: false, helperText: '\u00A0' }

  const defaultDateFormat = useMemo(
    () => getDateFormatFromLocale(currentLanguage),
    [currentLanguage],
  )
  const defaultDateMask = useMemo(
    () => getDateMaskFromFormat(defaultDateFormat),
    [defaultDateFormat],
  )

  const [selectedDate, setSelectedDate] = useState(startDate)
  const [parsedDate, setParsedDate] = useState(/** @type {Date|null} */ null)
  const [error, setError] = useState(defaultErrorState)

  const minDate = useMemo(() => (props.minDate
    ? new Date(Date.parse(props.minDate))
    : fromToday ? now : null), [props.minDate, fromToday])

  const maxDate = useMemo(() => (props.maxDate
    ? new Date(Date.parse(props.maxDate))
    : untilToday ? now : null), [props.maxDate, untilToday])

  useEffect(() => {
    startDate
      && startDate !== selectedDate
      && setSelectedDate(startDate)
  }, [startDate])

  useEffect(() => {
    setSelectedDate(value ? new Date(value) : defaultDate)
  }, [value])

  useEffect(() => {
    setError({ error: props.error, helperText: props.helperText || '\u00A0' })
  }, [props.helperText, props.error])

  const handleDateChange = useCallback((date, raw) => {
    setError(defaultErrorState)
    try {
      // Validating `date`
      const isInvalid = date.toString() === 'Invalid Date' || Number.isNaN(date.getTime())
      const wrongMinDate = !isInvalid && minDate ? isOlderThan(date, minDate) : false
      const wrongMaxDate = !isInvalid && maxDate ? isOlderThan(maxDate, date) : false

      let hasError = false

      if (isInvalid) {
        setError({ error: true, helperText: invalidLabel })
        hasError = true
      }

      let sanitizedDate
      if (wrongMinDate || wrongMaxDate) {
        sanitizedDate = sanitizeDateToMask(defaultDateMask, raw)
        if (isOlderThan(sanitizedDate, minDate) || isOlderThan(maxDate, sanitizedDate)) {
          setError({ error: true, helperText: outOfRangeLabel })
          hasError = true
        }
      }

      const targetDate = wrongMinDate || wrongMaxDate ? new Date(Date.parse(sanitizedDate)) : date
      const isoDate = targetDate ? getDateWithoutTimezone(targetDate).toISOString() : null

      !hasError && setParsedDate(targetDate)
      // If `raw` isn't defined, we didn't paste anything in, but selected a date from the picker
      // In that case, update the input
      !hasError && !raw && setSelectedDate(targetDate)

      if (!hasError) {
        const event = new CustomEvent('change', { bubbles: true })
        const platformEvent = new PlatformEvent(event, { value: isoDate })
        startTransition(() => events.onChange(platformEvent))
      }
    } catch (ignored) {
      // It is not an error to have incompletely written dates, but normal if the user wants to
      // write them by hand; we just neither send an event nor store it in the component's state
    }
  }, [minDate, maxDate])

  useEffect(() => {
    // propagate the default date on initial render if we have no value
    if (defaultDate && !value) {
      handleDateChange(defaultDate, null)
    }
  }, [])

  const handleDateOpen = useCallback(() => {
    parsedDate && setSelectedDate(parsedDate)
  }, [parsedDate])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        fullWidth
        id={'date-picker-dialog'}

        placeholder={'T'}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        label={props.label}
        value={selectedDate}
        onChange={handleDateChange}
        onOpen={handleDateOpen}
        mask={props.mask || defaultDateMask}
        inputFormat={props.format || defaultDateFormat} // this has to be i18n
        InputAdornmentProps={{ sx: { flex: 'auto 0 0', width: 36 } }}
        OpenPickerButtonProps={{ 'aria-label': 'Change date' }}
        components={{
          OpenPickerIcon: () => (
            <Icon
              size={20}
              name={'calendar'}
              color={'black.main'}
            />
          ),
        }}
        renderInput={params => (
          // this has to be a controlled component; we cannot use our TextField here because it's
          // setting its defaultValue prop - a controlled component must set its value prop
          <TextField
            {...params}
            fullWidth
            ref={forwardedRef}

            variant={'standard'}
            autoFocus={props.focused}
            autoComplete={props.autoComplete}
            inputProps={{
              ...params.inputProps,
              placeholder: props.placeholder,
            }}

            error={error.error}
            helperText={error.helperText}
            sx={{ flex: '100% 1 1' }}
          />
        )}
      />
    </LocalizationProvider>
  )
}

export default useMemoRef(DatePickerField, props => [
  props.value,
  props.label,
  props.helperText,
  props.error,
  props.required,
])
